import type { Product } from "./product-types";

export const PRODUCT_ERROR_COLUMN_KEYS = ["errors", "errors_text"] as const;
export const PRODUCT_MODERATION_CACHE_KEY = "product_moderation_snapshot";
export const PRODUCT_MODERATION_SNAPSHOT_VERSION = 1;
const ERROR_TEXT_FIELDS = ["description", "message", "attribute_name"] as const;

/** Missing/malformed source is unknown, distinct from an explicit empty array.
 * Only plain reported strings are accepted; no inferred moderation reasons. */
export function isProductErrors(value: unknown): value is NonNullable<Product["errors"]> {
  return Array.isArray(value) && value.every(error => error !== null && typeof error === "object"
    && !Array.isArray(error) && typeof error.code === "string"
    && (error.field === undefined || typeof error.field === "string")
    && (error.description === undefined || typeof error.description === "string")
    && (error.texts === undefined || error.texts !== null && typeof error.texts === "object" && !Array.isArray(error.texts)
      && ERROR_TEXT_FIELDS.every(key => error.texts[key] === undefined || typeof error.texts[key] === "string")));
}

/** Flatten compatible flat/nested reported text into the existing canonical
 * description. Preserve distinct source strings exactly, omit blank/repeated
 * strings, and never generate a cause or a suggested fix. Persistence and
 * report readers continue to consume the same narrow canonical shape. */
export function normalizeProductErrors(value: unknown): Product["errors"] {
  if (!isProductErrors(value)) return undefined;
  return value.map(error => {
    const nested = (error as typeof error & { texts?: Record<string, unknown> }).texts;
    const texts = [error.description, ...ERROR_TEXT_FIELDS.map(key => nested?.[key])]
      .filter((text): text is string => typeof text === "string" && text.trim().length > 0);
    const description = [...new Set(texts)].join(" · ");
    return { code: error.code,
      ...(error.field !== undefined ? { field: error.field } : {}),
      ...(description ? { description } : {}),
    };
  });
}

/** Plain text from Ozon's reported error fields, shared by cells, sorting and
 * export. Keep each error on its own line; do not infer a moderation cause. */
export function productErrorsText(errors: Product["errors"]): string {
  return (normalizeProductErrors(errors) ?? []).map(error => {
    const code = error.code || "";
    const field = error.field ? `[${error.field}]` : "";
    const identity = [code, field].filter(Boolean).join(" ");
    return [identity, error.description].filter(Boolean).join(": ");
  }).filter(Boolean).join("\n");
}
